/**
 * Theme Context and Provider
 * Provides theme management functionality with persistent storage and dynamic theme application
 * Supports multiple themes through external theme.json configuration
 */

import React, { createContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";

/**
 * Context for theme-related values and functions
 * @type {React.Context}
 */
export const ThemeContext = createContext();

/**
 * Theme Provider component that manages theme state and application
 * Handles theme persistence, loading from external source, and CSS variable application
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider
 * @returns {JSX.Element} Theme provider component
 */
const ThemeProvider = ({ children }) => {
  /**
   * Theme state initialization
   * Retrieves saved theme from localStorage or defaults to 'light'
   */
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("app-theme");
    return savedTheme ? savedTheme : "light";
  });

  /** State to store theme configurations loaded from theme.json */
  const [themes, setThemes] = useState({});
  const config = useSelector((state) => state.config);

  /**
   * Effect hook to fetch and apply theme configuration
   * Loads theme data from config and applies it to the application
   */
  useEffect(() => {
    const fetchAndApplyTheme = async () => {
      if (config?.clientConfig?.data?.collections && theme) {
        const themeObj = {};

        config.clientConfig.data.theming.themes.forEach((theme) => {
          themeObj[theme.name] = {
            name: theme.name,
            colors: {
              primary: theme.colors.primary,
              secondary: theme.colors.secondary,
              accent: theme.colors.accent,
              neutral: theme.colors.neutral,
            },
            bg: {
              primary: theme.bg.primary,
              secondary: theme.bg.secondary,
              accent: theme.bg.accent,
              neutral: theme.bg.neutral,
            },
            text: {
              primary: theme.text.primary,
              secondary: theme.text.secondary,
              accent: theme.text.accent,
              neutral: theme.text.neutral,
            },
            border: {
              primary: theme.border.primary,
              secondary: theme.border.secondary,
              accent: theme.border.accent,
              neutral: theme.border.neutral,
            },
          };
        });

        setThemes(themeObj);
        applyTheme(themeObj[theme]);
      }
    };

    fetchAndApplyTheme();
  }, [config, theme]);

  /**
   * Applies theme variables to document root
   * Sets CSS custom properties based on theme configuration
   * @param {Object} themeVariables - Object containing theme CSS variable definitions
   */
  const applyTheme = (themeVariables) => {
    const root = document.documentElement;

    // Theme Colors
    root.style.setProperty(
      "--theme-color-primary",
      themeVariables.colors.primary,
    );
    root.style.setProperty(
      "--theme-color-secondary",
      themeVariables.colors.secondary,
    );
    root.style.setProperty(
      "--theme-color-accent",
      themeVariables.colors.accent,
    );
    root.style.setProperty(
      "--theme-color-neutral",
      themeVariables.colors.neutral,
    );

    // Background colors
    root.style.setProperty("--theme-bg-primary", themeVariables.bg.primary);
    root.style.setProperty("--theme-bg-secondary", themeVariables.bg.secondary);
    root.style.setProperty("--theme-bg-accent", themeVariables.bg.accent);
    root.style.setProperty("--theme-bg-neutral", themeVariables.bg.neutral);

    // Text colors
    root.style.setProperty("--theme-text-primary", themeVariables.text.primary);
    root.style.setProperty(
      "--theme-text-secondary",
      themeVariables.text.secondary,
    );
    root.style.setProperty("--theme-text-accent", themeVariables.text.accent);
    root.style.setProperty("--theme-text-neutral", themeVariables.text.neutral);

    // Border colors
    root.style.setProperty(
      "--theme-border-primary",
      themeVariables.border.primary,
    );
    root.style.setProperty(
      "--theme-border-secondary",
      themeVariables.border.secondary,
    );
    root.style.setProperty(
      "--theme-border-accent",
      themeVariables.border.accent,
    );
    root.style.setProperty(
      "--theme-border-neutral",
      themeVariables.border.neutral,
    );
  };

  /**
   * Effect hook to persist theme changes
   * Applies theme variables and saves selection to localStorage
   */
  useEffect(() => {
    if (themes[theme]) {
      applyTheme(themes[theme]);
    }
    localStorage.setItem("app-theme", theme);
  }, [theme, themes]);

  /**
   * Changes the current theme
   * @param {string} themeName - Name of the theme to apply
   */
  const changeTheme = (themeName) => {
    setTheme(themeName);
  };

  return (
    <ThemeContext.Provider value={{ theme, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
